import { useCallback, useEffect, useRef, useState } from "react";

import { Form, Input, InputRef } from "antd";

import { RVFormCheckbox, RVFormItem, RVModal } from "@regal-voice/ui";
import { SavedView } from "Types/SavedViews";

import type { ContactListModalSavedView } from "Pages/agentDesktop/Pages/AgentDesktopTasks/Components/AgentTaskInterface/AgentPanes/AgentActionPaneSelection/ContactsPanel/Components/SavedViewContactsPanel/queries";

type SaveViewModalProps = {
    viewBeingEdited?: ContactListModalSavedView | SavedView;
    onCancel: () => void;
    onSave: (savedView: ContactListModalSavedView) => void;
};

export function SaveViewModal({ onCancel, onSave, viewBeingEdited }: SaveViewModalProps): JSX.Element {
    const [form] = Form.useForm();
    const [loading, setIsLoading] = useState(false);
    const inputEl = useRef<InputRef>(null);

    const onFinish = useCallback(
        async (view: ContactListModalSavedView) => {
            setIsLoading(true);

            onSave({
                ...view,
                name: view.name?.trim(),
            } as ContactListModalSavedView);

            setIsLoading(false);
        },
        [setIsLoading, onSave]
    );

    const handleOnCancel = useCallback(() => {
        form.resetFields();
        onCancel();
    }, [form, onCancel]);

    const submitForm = useCallback(async () => {
        async function formHasErrors() {
            try {
                await form.validateFields();
                return false;
                // update type to unknown and use normalizeError
            } catch (err: any) {
                return Boolean(err.errorFields?.some(({ errors }: { errors: Array<string> }) => errors.length));
            }
        }

        const hasErrors = await formHasErrors();
        if (!hasErrors) {
            form.submit();
        }
    }, [form]);

    useEffect(() => {
        if (inputEl.current) {
            inputEl.current.focus();
        }
    }, []);

    return (
        <RVModal
            title={`${viewBeingEdited ? "Update" : "New"} View`}
            open={true}
            onCancel={handleOnCancel}
            onOk={submitForm}
            okButtonProps={{ loading }}
            destroyOnClose={true}
        >
            <Form layout="vertical" onFinish={onFinish} form={form} initialValues={viewBeingEdited}>
                <RVFormItem hidden={true} name="id">
                    <Input type="hidden" />
                </RVFormItem>

                <RVFormItem hidden={true} name="page">
                    <Input type="hidden" />
                </RVFormItem>

                <RVFormItem hidden={true} name="pageQuery">
                    <Input type="hidden" />
                </RVFormItem>

                <RVFormItem
                    label="View Name"
                    required={true}
                    name="name"
                    rules={[{ required: true, message: "View Name is a required field." }]}
                >
                    <Input placeholder="View Name" autoComplete="off" ref={inputEl} />
                </RVFormItem>

                {viewBeingEdited && <RVFormCheckbox name="isDefault">Set as default view</RVFormCheckbox>}
            </Form>
        </RVModal>
    );
}
